// - TODO unterscheide $t('no-scenario-loaded') und $t('scenario-not-found')

import { AllocationGroupType } from '@/components/AllocationSelectionPanel/AllocationSelectionPanel.types';
import { TIMELINE_AREA_TYPES, TIMELINE_ROOM_TYPES } from '@/components/Timeline/Timeline.types';
import {
    CreateScenarioSettingDocument,
    DesignerScenarioFragment,
    DesignerScenarioSettingFragment,
    GetDesignerScenarioDocument,
    GetEventsInTimeRangeDocument,
    GetExternalEventsInTimeRangeDocument,
    GetProjectsInTimeRangeDocument,
    GetScenarioSettingByScenarioIdDocument,
    GetSectionsDataForAllocationDesignerTimelineDocument,
    GetSectionsDataForAllocationDesignerTimelineQuery,
    UpdateScenarioSettingDocument,
    UpdateScenarioVisibilitiesDocument,
    VariantType,
} from '@/generated/graphql';
import { getDateTimeFromYearAndQuarter } from '@/navigation/utils';
import { $t } from '@/plugins/fluent';
import { ApolloClient } from '@apollo/client';
import { DateTime, QuarterNumbers } from 'luxon';
import { ActorRefFrom, assign, fromPromise, setup } from 'xstate';
import { FilterContext, FilterMachineType, VariantStateVisibility } from './AllocationSelectionFilter.state';
import {
    calculateDisplayedStart,
    calculateEndFromStart,
    onChangeQuarter,
    onChangeYear,
} from './composables/designerMachineFunctions';

const fetchScenarioActor = fromPromise(
    async ({ input }: { input: { scenarioId: string; client: ApolloClient<any> } }) => {
        const result = await input.client.query({
            query: GetDesignerScenarioDocument,
            variables: { scenarioId: input.scenarioId },
        });
        if (result.errors) throw result.errors;
        return result.data.scenario ?? undefined;
    },
);

const fetchScenarioSettingActor = fromPromise(
    async ({ input }: { input: { scenarioId: string; client: ApolloClient<any> } }) => {
        const result = await input.client.query({
            query: GetScenarioSettingByScenarioIdDocument,
            variables: { scenarioId: input.scenarioId },
        });
        if (result.errors) throw result.errors;
        return result.data.scenarioSetting ?? undefined;
    },
);

const fetchTimelineDataActor = fromPromise(
    async ({ input }: { input: { startDateTime: string; endDateTime: string; client: ApolloClient<any> } }) => {
        const result = await input.client.query({
            query: GetSectionsDataForAllocationDesignerTimelineDocument,
            variables: {
                startDateTime: input.startDateTime,
                endDateTime: input.endDateTime,
                roomTypes: TIMELINE_ROOM_TYPES,
                areaTypes: TIMELINE_AREA_TYPES,
            },
        });
        if (result.errors) throw result.errors;
        return result.data ?? undefined;
    },
);

const fetchEventFilterDataActor = fromPromise(
    async ({ input }: { input: { startDateTime: string; endDateTime: string; client: ApolloClient<any> } }) => {
        const result = await input.client.query({
            query: GetEventsInTimeRangeDocument,
            variables: { start: input.startDateTime, end: input.endDateTime },
        });
        if (result.errors) throw result.errors;
        return result.data ?? undefined;
    },
);

const fetchProjectFilterDataActor = fromPromise(
    async ({ input }: { input: { startDateTime: string; endDateTime: string; client: ApolloClient<any> } }) => {
        const result = await input.client.query({
            query: GetProjectsInTimeRangeDocument,
            variables: { start: input.startDateTime, end: input.endDateTime },
        });
        if (result.errors) throw result.errors;
        return result.data ?? undefined;
    },
);

const fetchExternalEventFilterDataActor = fromPromise(
    async ({ input }: { input: { startDateTime: string; endDateTime: string; client: ApolloClient<any> } }) => {
        const result = await input.client.query({
            query: GetExternalEventsInTimeRangeDocument,
            variables: { start: input.startDateTime, end: input.endDateTime },
        });
        if (result.errors) throw result.errors;
        return result.data ?? undefined;
    },
);

interface DesignerMachineContext {
    scenarioId: string;
    client: ApolloClient<any>;
    year: string;
    quarter: string;
    timelineDisplay: string;
    start: DateTime;
    end: DateTime;
    monthsBefore: number;
    monthsAfter: number;
    timelineData?: GetSectionsDataForAllocationDesignerTimelineQuery;
    scenario?: DesignerScenarioFragment;
    setting?: DesignerScenarioSettingFragment;
    loadScenarioError: string;
    loadSettingError: string;
    loadingTimelineError: string;
    tabTitle: string;
    warningNoAllocations: {
        buttonJumpToPreviousAllocations: 'Hidden' | 'Disabled' | 'Visible';
        buttonJumpToNextAllocations: 'Hidden' | 'Disabled' | 'Visible';
    } | null;
    eventFilterRef: ActorRefFrom<FilterMachineType>;
    projectFilterRef: ActorRefFrom<FilterMachineType>;
    externalEventFilterRef: ActorRefFrom<FilterMachineType>;
    scenarioSaved: boolean;
}

const createScenarioSettingActor = fromPromise(
    async ({
        input,
    }: {
        input: {
            client: ApolloClient<any>;
            context: DesignerMachineContext;
        };
    }) => {
        if (input.context.scenario && input.context.start && input.context.end) {
            const result = await input.client.mutate({
                mutation: CreateScenarioSettingDocument,
                variables: {
                    scenarioId: input.context.scenario.id,
                    year: Number(input.context.year),
                    quarter: Number(input.context.quarter),
                    start: input.context.start.toString(),
                    end: input.context.end.toString(),
                    monthsAfter: input.context.monthsAfter,
                    monthsBefore: input.context.monthsBefore,
                    showSections: Boolean(input.context.timelineDisplay === 'sections'),
                },
            });
            if (result.errors) throw new Error('could not save scenario setting');

            return result.data?.createScenarioSetting?.scenarioSetting ?? undefined;
        }
        throw new Error('create scenario params missing');
    },
);

function getVariantType(type: AllocationGroupType) {
    switch (type) {
        case 'Event':
            return VariantType.Event;
        case 'Project':
            return VariantType.Project;
        case 'ExternalEvent':
            return VariantType.ExternalEvent;
    }
}

function getAllocationGroupType(type: VariantType) {
    switch (type) {
        case VariantType.Event:
            return 'Event' as const;
        case VariantType.Project:
            return 'Project' as const;
        case VariantType.ExternalEvent:
            return 'ExternalEvent' as const;
    }
}

const updateScenarioVisibilitiesActor = fromPromise(
    async ({
        input,
    }: {
        input: {
            client: ApolloClient<any>;
            context: DesignerMachineContext;
            eventFilterContext: FilterContext;
            projectFilterContext: FilterContext;
            externalEventFilterContext: FilterContext;
        };
    }) => {
        // const defaultVisibilities = [
        //     ...input.eventFilterContext.defaultStates,
        //     ...input.projectFilterContext.defaultStates,
        //     ...input.externalEventFilterContext.defaultStates,
        // ];
        const changedVisibilities = [
            ...input.eventFilterContext.changedStates,
            ...input.projectFilterContext.changedStates,
            ...input.externalEventFilterContext.changedStates,
        ];

        if (input.context.scenario) {
            const scenarioId = input.context.scenario.id;
            const scenarioVisibilities = input.context.scenario.variantVisibilities.nodes;

            let deleteById = [];
            let create = [];

            for (const vis of changedVisibilities) {
                const existingScenarioVis = scenarioVisibilities.find(
                    (v) => v.variantId === vis.variantId && v.visibility !== vis.visibility,
                );
                if (existingScenarioVis) {
                    // delete
                    deleteById.push({ id: existingScenarioVis.id });
                } else {
                    // create
                    create.push({
                        variantId: vis.variantId,
                        visibility: vis.visibility,
                        variantType: getVariantType(vis.variantType),
                    });
                }
            }

            const result = await input.client.mutate({
                mutation: UpdateScenarioVisibilitiesDocument,
                variables: {
                    scenarioId: scenarioId,
                    create: create,
                    deleteById: deleteById,
                },
            });
            if (result.errors) throw new Error('failed to update scenario visibilities');
            return result.data?.updateScenario?.scenario ?? undefined;
        }
        throw new Error('cannot create visibilities for undefined scenario');
    },
);

const updateScenarioSettingActor = fromPromise(
    async ({
        input,
    }: {
        input: {
            client: ApolloClient<any>;
            context: DesignerMachineContext;
        };
    }) => {
        const scenario = input.context.scenario;
        const setting = input.context.setting;

        if (scenario && setting) {
            const result = await input.client.mutate({
                mutation: UpdateScenarioSettingDocument,
                variables: {
                    scenarioId: scenario.id,
                    year: Number(input.context.year),
                    quarter: Number(input.context.quarter),
                    start: input.context.start?.toString(),
                    end: input.context.end?.toString(),
                    monthsAfter: input.context.monthsAfter,
                    monthsBefore: input.context.monthsBefore,
                    showSections: Boolean(input.context.timelineDisplay === 'sections'),
                },
            });
            if (result.errors) throw new Error('could not save scenario setting');
            return {
                setting: result.data?.updateScenarioSetting?.scenarioSetting ?? undefined,
            };
        }
        throw new Error('update scenario params missing');
    },
);

export type DesignerMachineEvents =
    | { type: 'with.nothing' }
    | { type: 'with.routeparams'; quarter: string; year: string; timelineDisplay: string }
    | { type: 'with.scenario' }
    | { type: 'change.quarter'; quarter: QuarterNumbers }
    | { type: 'change.year'; year: number }
    | { type: 'click.previousQuarter' }
    | { type: 'click.nextQuarter' }
    | { type: 'click.nextMonth' }
    | { type: 'click.previousMonth' }
    | { type: 'change.timelineDisplay'; timelineDisplay: string }
    | { type: 'change.visibility' }
    | { type: 'save.scenario' }
    | { type: 'discard.scenario.changes' }
    | { type: 'unset.scenario.saved' };

export const createDesignerStateMachine = () => {
    return setup({
        types: {} as {
            input: {
                scenarioId: string;
                client: ApolloClient<any>;
                eventFilterRef: ActorRefFrom<FilterMachineType>;
                projectFilterRef: ActorRefFrom<FilterMachineType>;
                externalEventFilterRef: ActorRefFrom<FilterMachineType>;
            };
            context: DesignerMachineContext;
            events: DesignerMachineEvents;
        },
        actors: {
            fetchScenario: fetchScenarioActor,
            fetchScenarioSetting: fetchScenarioSettingActor,
            fetchTimelineData: fetchTimelineDataActor,
            fetchEventFilterData: fetchEventFilterDataActor,
            fetchProjectFilterData: fetchProjectFilterDataActor,
            fetchExternalEventFilterData: fetchExternalEventFilterDataActor,
            createScenarioSetting: createScenarioSettingActor,
            updateScenarioSetting: updateScenarioSettingActor,
            updateScenarioVisibilities: updateScenarioVisibilitiesActor,
        },
        guards: {
            isScenarioDefined: ({ context }) => {
                return context.scenario !== undefined;
            },
            isTimeRangeSet: ({ context }) => {
                return context.start !== undefined && context.end !== undefined;
            },
            isTimelineDataDefined: ({ context }) => {
                return context.timelineData !== undefined;
            },
        },
    }).createMachine({
        /** @xstate-layout N4IgpgJg5mDOIC5QTgSygOzAJwHQBsB7AQwlQygGIB3VAFwAtcNDHyoBtABgF1FQADoVj1UhDPxAAPRAEYAbLNwAmLmq6yArLOXLZADmWaANCACecgCzLcATnXL9srrYDMtp5YC+X0yhGYOAQkZBQ09EzYhACudGACxNjEALaw3HxIIEIidGISmTIIsgDsSqrqWjp6hibmciWuuPqW2pZcjsrytsU+fmiBeESk7OGMuLAAxmAYiWLpktmi4pKFJWXqGtq6BkamFgiWskpu8l3Fapa2HpryvSD+6Fh4LABKMXEACokpsAByhABlKYzbBiSjzTKLXLLApyPS2XDFeRGJxOLinWR7RBOFRXK6WfTFVzFWzWfR3B4DXC0RhA6azQjBYZhCDiMC4cgAN0IAGt2UMIHSQXNeAthEt8qBCvIkbh5ITNJZFJpbHouPosQcXHZbJp2vIVcpiiryb57v0ntSIkKGUzQlQcFE8AJ8MQ6AAzQjYZJ2m2gwgQwTi6GS6Rw4r6XAteSudWuVx6zTFZSao1cJrlIwaZyWYpEikWoI0hh+sS4cQAETZdpGrKwHIw3L5vuBDIBYDouQogaywbyK2xXGKGb1CeRmn0Ktq+268h1evkQ+0Bss3jNlMtxdLjMr1YFI0dXtwLrdnu9Lfp-vbnfYPah-dhB1sc-H7UObWUlncmsnmlwOjUVV9H0VwZWfHwzRYfxJA3HAxRyB8pUQABaeRNWQv88SuElTkVUplALAJLX3Ch4IlAcDhTOoij0Rorm0ZwtGUZ8uE0QjHiCV53jAL4klSf5tzIkMKNcLRcHRIwyRJK4nE1WRrB1PF5P0Fc8PYqlizeWIeO+VIhMQsMijaSMKnRLpdWKJU5L0OdiWzVQiUXBR1M3a1W39fSYSQhBRMaGoNFzUliQMOSQNwTRylcPRznOGMCPXQs8C3dyyxIqBPNDQpQLnfz5JJL9Sg1aj3D-do1FArg2gnVw1z6Iiizcy8y13LAMpEmMRwC-LgqK-YkWHQw1AXYCJNkFyGtpFKdwwKt6zStrH0OJRcsCgqQuo3UbFzbN5VJNoDHGpLGuFabZvZFqwAW7yc3TG5OiOPVV0s4pNRkqNVCOdFrFKcCEvqo7JqaxlYGITlLshPsvMM0DlqzPKgsKzUIts8ols0G4JzYv6OIBkspuYVgADEYgwCAruh5M7FKPMCU-WRXBAuSShseMk11eNk0MHoIKAA */
        id: 'designer',
        initial: 'initial',
        context: ({ input }) => {
            const now = DateTime.now();
            return {
                scenarioId: input.scenarioId,
                client: input.client,
                year: '',
                quarter: '',
                timelineDisplay: '',
                start: calculateDisplayedStart(now, 0),
                end: calculateEndFromStart(now, 0),
                monthsBefore: 0,
                monthsAfter: 0,
                timelineData: undefined,
                scenario: undefined,
                setting: undefined,
                loadScenarioError: '',
                loadSettingError: '',
                tabTitle: $t('allocation-designer'),
                warningNoAllocations: null,
                loadingTimelineError: '',
                eventFilterRef: input.eventFilterRef,
                projectFilterRef: input.projectFilterRef,
                externalEventFilterRef: input.externalEventFilterRef,
                scenarioSaved: false,
            } as DesignerMachineContext;
        },
        states: {
            initial: {
                on: {
                    'with.nothing': {
                        actions: [
                            assign(({ context }) => {
                                const now = DateTime.now();
                                return {
                                    ...context,
                                    quarter: now.quarter.toString(),
                                    year: now.year.toString(),
                                    timelineDisplay: 'sections',
                                    tabTitle: $t('allocation-designer'),
                                    start: calculateDisplayedStart(now, 0),
                                    end: calculateEndFromStart(now, 0),
                                    monthsAfter: 0,
                                    monthsBefore: 0,
                                };
                            }),
                        ],
                        target: 'noRouteParamsNoScenario',
                    },
                    'with.routeparams': {
                        actions: [
                            assign(({ context, event }) => {
                                const calcStart = calculateDisplayedStart(
                                    getDateTimeFromYearAndQuarter(event.year, event.quarter) ?? DateTime.now(),
                                    0,
                                );
                                return {
                                    ...context,
                                    quarter: event.quarter,
                                    year: event.year,
                                    timelineDisplay: event.timelineDisplay,
                                    start: calcStart,
                                    end: calculateEndFromStart(calcStart, 0),
                                    monthsAfter: 0,
                                    monthsBefore: 0,
                                };
                            }),
                        ],
                        target: 'withRouteParams',
                    },
                    'with.scenario': { target: 'withScenario.loading' },
                },
            },

            withRouteParams: {
                always: {
                    guard: 'isTimeRangeSet',
                    target: 'loadData',
                },
            },

            noRouteParamsNoScenario: {
                always: {
                    guard: 'isTimeRangeSet',
                    target: 'loadData',
                },
            },

            withScenario: {
                initial: 'loading',
                states: {
                    loading: {
                        entry: [
                            assign(({ context }) => {
                                return {
                                    ...context,
                                    tabTitle: `${$t('scenario-loading')} - ${$t('allocation-designer')}`,
                                };
                            }),
                        ],
                        invoke: {
                            id: 'loadScenario',
                            src: 'fetchScenario',
                            input: ({ context }) => ({
                                scenarioId: context.scenarioId,
                                client: context.client,
                            }),
                            onDone: [
                                {
                                    guard: ({ event }) => event.output === undefined,
                                    target: 'scenarioLoadingFailed',
                                },
                                {
                                    guard: ({ event }) => event.output !== undefined,
                                    actions: assign(({ context, event }) => {
                                        return {
                                            ...context,
                                            scenario: event.output ?? undefined,
                                        };
                                    }),
                                    target: 'scenarioLoaded',
                                },
                            ],
                            onError: {
                                actions: assign(({ context, event }) => {
                                    return {
                                        ...context,
                                        loadScenarioError: event.error as string,
                                        tabTitle: `${$t('scenario-not-found')} - ${$t('allocation-designer')}`,
                                    };
                                }),
                                target: 'scenarioLoadingFailed',
                            },
                        },
                    },

                    scenarioLoadingFailed: {
                        type: 'final',
                    },

                    scenarioLoaded: {
                        initial: 'loading',
                        states: {
                            loading: {
                                entry: assign(({ context }) => {
                                    return {
                                        tabTitle: `${context.scenario?.nameShort} - ${$t('allocation-designer')}`,
                                    };
                                }),
                                invoke: {
                                    id: 'loadScenarioSetting',
                                    src: 'fetchScenarioSetting',
                                    input: ({ context }) => ({
                                        scenarioId: context.scenarioId,
                                        client: context.client,
                                    }),
                                    onDone: [
                                        {
                                            guard: ({ event }) => event.output === undefined,
                                            target: 'settingNotFound',
                                        },
                                        {
                                            guard: ({ event }) => event.output !== undefined,
                                            target: 'settingLoaded',
                                            actions: assign({ setting: ({ event }) => event.output ?? undefined }),
                                        },
                                    ],
                                    onError: {
                                        actions: assign(({ context, event }) => {
                                            return {
                                                ...context,
                                                loadSettingError: event.error as string,
                                            };
                                        }),
                                    },
                                },
                            },
                            settingLoaded: {
                                entry: [
                                    assign(({ context }) => {
                                        if (context.setting) {
                                            return {
                                                start: DateTime.fromISO(context.setting.start),
                                                end: DateTime.fromISO(context.setting.end),
                                                quarter: context.setting.quarter.toString(),
                                                year: context.setting.year.toString(),
                                                monthsAfter: context.setting.monthsAfter,
                                                monthsBefore: context.setting.monthsBefore,
                                                timelineDisplay: context.setting.showSections
                                                    ? 'sections'
                                                    : 'hierarchy',
                                                tabTitle: `${context.scenario?.nameShort ?? ''} - ${$t(
                                                    'allocation-designer',
                                                )}`,
                                            };
                                        }
                                        return context;
                                    }),
                                ],
                                always: 'checkTimeRangeSet',
                            },
                            settingNotFound: {
                                entry: [
                                    assign(({ context }) => {
                                        // a scenario without setting starts in the scenario.year
                                        if (context.scenario) {
                                            const scenarioYear = context.scenario?.year;
                                            const scenarioStart = DateTime.fromISO(`${scenarioYear}-01-01`);
                                            const defaultStart = calculateDisplayedStart(scenarioStart, 0);
                                            return {
                                                ...context,
                                                start: defaultStart,
                                                end: calculateEndFromStart(defaultStart, 0),
                                                year: defaultStart.year.toString(),
                                                quarter: defaultStart.quarter.toString(),
                                                monthsBefore: 0,
                                                monthsAfter: 0,
                                                timelineDisplay: 'sections',
                                                tabTitle: `${context.scenario?.nameShort ?? ''} - ${$t(
                                                    'allocation-designer',
                                                )}`,
                                            };
                                        }
                                        return context;
                                    }),
                                ],
                                always: 'checkTimeRangeSet',
                            },
                            checkTimeRangeSet: {
                                always: {
                                    guard: 'isTimeRangeSet',
                                    target: '#designer.loadData',
                                },
                            },
                        },
                    },
                },
            },

            errorOnSaveScenarioSetting: {},
            errorOnUpdateVisibilities: {},

            createSetting: {
                invoke: {
                    src: 'createScenarioSetting',
                    input: ({ context }) => ({
                        client: context.client,
                        context,
                    }),
                    onDone: {
                        actions: [
                            assign(({ context, event }) => {
                                if (event.output) {
                                    return {
                                        ...context,
                                        setting: event.output,
                                    };
                                }
                                return context;
                            }),
                        ],
                        target: 'updateVisibilities',
                    },
                    onError: {
                        target: 'errorOnSaveScenarioSetting',
                    },
                },
            },

            updateSetting: {
                invoke: {
                    src: 'updateScenarioSetting',
                    input: ({ context }) => ({
                        client: context.client,
                        context,
                    }),
                    onDone: {
                        actions: [
                            assign(({ context, event }) => {
                                if (event.output) {
                                    return {
                                        ...context,
                                        setting: event.output.setting,
                                    };
                                }
                                return context;
                            }),
                        ],
                        target: 'updateVisibilities',
                    },
                    onError: {
                        target: 'errorOnSaveScenarioSetting',
                    },
                },
            },

            updateVisibilities: {
                invoke: {
                    src: 'updateScenarioVisibilities',
                    input: ({ context }) => ({
                        client: context.client,
                        context,
                        eventFilterContext: context.eventFilterRef.getSnapshot().context,
                        projectFilterContext: context.projectFilterRef.getSnapshot().context,
                        externalEventFilterContext: context.externalEventFilterRef.getSnapshot().context,
                    }),
                    onDone: {
                        actions: [
                            assign(({ context, event }) => {
                                if (event.output) {
                                    context.eventFilterRef.send({
                                        type: 'set.scenario.visibilities',
                                        visibilities:
                                            event.output?.scenarioVariantVisibilities.nodes
                                                .filter((v) => v.variantType === VariantType.Event)
                                                .map((v) => ({
                                                    ...v,
                                                    visibility: v.visibility as VariantStateVisibility,
                                                    variantType: getAllocationGroupType(v.variantType),
                                                })) ?? [],
                                    });
                                    context.projectFilterRef.send({
                                        type: 'set.scenario.visibilities',
                                        visibilities:
                                            event.output?.scenarioVariantVisibilities.nodes
                                                .filter((v) => v.variantType === VariantType.Project)
                                                .map((v) => ({
                                                    ...v,
                                                    visibility: v.visibility as VariantStateVisibility,
                                                    variantType: getAllocationGroupType(v.variantType),
                                                })) ?? [],
                                    });
                                    context.externalEventFilterRef.send({
                                        type: 'set.scenario.visibilities',
                                        visibilities:
                                            event.output?.scenarioVariantVisibilities.nodes
                                                .filter((v) => v.variantType === VariantType.ExternalEvent)
                                                .map((v) => ({
                                                    ...v,
                                                    visibility: v.visibility as VariantStateVisibility,
                                                    variantType: getAllocationGroupType(v.variantType),
                                                })) ?? [],
                                    });
                                    if (context.scenario) {
                                        return {
                                            ...context,
                                            scenario: {
                                                ...context.scenario,
                                                variantVisibilities: {
                                                    nodes: event.output.scenarioVariantVisibilities.nodes,
                                                },
                                            },
                                            scenarioSaved: true,
                                        };
                                    }
                                    return context;
                                }
                                return context;
                            }),
                        ],
                        target: 'loadData.loaded',
                    },
                    onError: {
                        target: 'errorOnUpdateVisibilities',
                    },
                },
            },

            loadData: {
                initial: 'loading',
                states: {
                    loading: {
                        type: 'parallel',
                        states: {
                            timelineData: {
                                initial: 'fetch',
                                states: {
                                    fetch: {
                                        invoke: {
                                            src: 'fetchTimelineData',
                                            input: ({ context }) => {
                                                const start = context.start.toISO();
                                                if (!start) {
                                                    throw new Error('Conversion toISO() failed for start');
                                                }

                                                const end = context.end.toISO();
                                                if (!end) {
                                                    throw new Error('Conversion toISO() failed for end');
                                                }

                                                return {
                                                    startDateTime: start,
                                                    endDateTime: end,
                                                    client: context.client,
                                                };
                                            },
                                            onDone: {
                                                actions: assign(({ context, event }) => {
                                                    return {
                                                        ...context,
                                                        timelineData: event.output ?? undefined,
                                                    };
                                                }),
                                                // guard: 'isTimelineDataDefined',
                                                target: 'done',
                                            },
                                            onError: {
                                                actions: ({ event }) => {
                                                    console.error('error on loading timelinedata', event.error);
                                                },
                                                target: '../loadingError',
                                            },
                                        },
                                    },
                                    done: {
                                        type: 'final',
                                    },
                                },
                            },
                            eventFilterData: {
                                initial: 'fetch',
                                states: {
                                    fetch: {
                                        invoke: {
                                            src: 'fetchEventFilterData',
                                            input: ({ context }) => ({
                                                startDateTime: context.start?.toISO() ?? '',
                                                endDateTime: context.end?.toISO() ?? '',
                                                client: context.client,
                                            }),
                                            onDone: {
                                                actions: [
                                                    ({ context, event }) => {
                                                        context.eventFilterRef.send({
                                                            type: 'set.data',
                                                            data: event.output,
                                                            start: context.start,
                                                            end: context.end,
                                                        });
                                                    },
                                                ],
                                                target: 'done',
                                            },
                                            onError: {
                                                actions: ({ event }) => {
                                                    console.error('error on loading event filter data', event.error);
                                                },
                                                target: '../loadingError',
                                            },
                                        },
                                    },
                                    done: {
                                        type: 'final',
                                    },
                                },
                            },
                            projectFilterData: {
                                initial: 'fetch',
                                states: {
                                    fetch: {
                                        invoke: {
                                            src: 'fetchProjectFilterData',
                                            input: ({ context }) => ({
                                                startDateTime: context.start?.toISO() ?? '',
                                                endDateTime: context.end?.toISO() ?? '',
                                                client: context.client,
                                            }),
                                            onDone: {
                                                actions: [
                                                    ({ context, event }) => {
                                                        context.projectFilterRef.send({
                                                            type: 'set.data',
                                                            data: event.output,
                                                            start: context.start,
                                                            end: context.end,
                                                        });
                                                    },
                                                ],
                                                target: 'done',
                                            },
                                            onError: {
                                                actions: ({ event }) => {
                                                    console.error('error on loading project filter data', event.error);
                                                },
                                                target: '../loadingError',
                                            },
                                        },
                                    },
                                    done: {
                                        type: 'final',
                                    },
                                },
                            },
                            externalEventFilterData: {
                                initial: 'fetch',
                                states: {
                                    fetch: {
                                        invoke: {
                                            src: 'fetchExternalEventFilterData',
                                            input: ({ context }) => ({
                                                startDateTime: context.start?.toISO() ?? '',
                                                endDateTime: context.end?.toISO() ?? '',
                                                client: context.client,
                                            }),
                                            onDone: {
                                                actions: [
                                                    ({ context, event }) => {
                                                        context.externalEventFilterRef.send({
                                                            type: 'set.data',
                                                            data: event.output,
                                                            start: context.start,
                                                            end: context.end,
                                                        });
                                                    },
                                                ],
                                                target: 'done',
                                            },
                                            onError: {
                                                actions: ({ event }) => {
                                                    console.error(
                                                        'error on loading external event filter data',
                                                        event.error,
                                                    );
                                                },
                                                target: '../loadingError',
                                            },
                                        },
                                    },
                                    done: {
                                        type: 'final',
                                    },
                                },
                            },
                        },
                        onDone: {
                            target: 'loaded',
                            actions: [
                                assign(({ context }) => {
                                    if (context.scenario) {
                                        context.eventFilterRef.send({
                                            type: 'set.scenario.visibilities',
                                            visibilities: context.scenario.variantVisibilities.nodes
                                                .filter((v) => v.variantType === VariantType.Event)
                                                .map((v) => ({
                                                    ...v,
                                                    visibility: v.visibility as VariantStateVisibility,
                                                    variantType: getAllocationGroupType(v.variantType),
                                                })),
                                        });
                                        context.projectFilterRef.send({
                                            type: 'set.scenario.visibilities',
                                            visibilities: context.scenario.variantVisibilities.nodes
                                                .filter((v) => v.variantType === VariantType.Project)
                                                .map((v) => ({
                                                    ...v,
                                                    visibility: v.visibility as VariantStateVisibility,
                                                    variantType: getAllocationGroupType(v.variantType),
                                                })),
                                        });
                                        context.externalEventFilterRef.send({
                                            type: 'set.scenario.visibilities',
                                            visibilities: context.scenario.variantVisibilities.nodes
                                                .filter((v) => v.variantType === VariantType.ExternalEvent)
                                                .map((v) => ({
                                                    ...v,
                                                    visibility: v.visibility as VariantStateVisibility,
                                                    variantType: getAllocationGroupType(v.variantType),
                                                })),
                                        });
                                    }
                                    return context;
                                }),
                            ],
                        },
                    },
                    loadingError: {},
                    loaded: {
                        on: {
                            'change.timelineDisplay': {
                                // show a different timeline view
                                // change: timelineDisplay
                                // unchanged: year, quarter, start, end, monthsBefore, monthsAfter
                                // reset: x
                                // reload: x
                                actions: [
                                    assign({
                                        timelineDisplay: ({ event }) => event.timelineDisplay,
                                        scenarioSaved: () => false,
                                    }),
                                ],
                            },
                            'change.quarter': {
                                // show a different quarter
                                // change: quarter, (year), start, end
                                // unchanged: x
                                // reset: monthsBefore, monthsAfter
                                // reload: timelineData, filterData
                                actions: [
                                    assign(({ context, event }) => {
                                        const oldQuarter = Number(context.quarter) as QuarterNumbers;
                                        const newQuarter = event.quarter;
                                        const newStart = onChangeQuarter(context.start, oldQuarter, newQuarter);
                                        if (newStart) {
                                            return {
                                                ...context,
                                                start: newStart,
                                                end: calculateEndFromStart(newStart, 0),
                                                quarter: newStart.quarter.toString(),
                                                year: newStart.year.toString(),
                                                monthsBefore: 0,
                                                monthsAfter: 0,
                                                scenarioSaved: false,
                                            };
                                        }
                                        return context;
                                    }),
                                ],
                                target: 'loading',
                            },
                            'change.year': {
                                // show a different year
                                // change: year, start, end
                                // unchanged: quarter
                                // reset: monthsBefore, monthsAfter
                                // reload: timelineData, filterData
                                actions: [
                                    assign(({ context, event }) => {
                                        const oldYear = Number(context.year);
                                        const newYear = event.year;
                                        const newStart = onChangeYear(context.start, oldYear, newYear);
                                        if (newStart) {
                                            return {
                                                ...context,
                                                start: newStart,
                                                end: calculateEndFromStart(newStart, 0),
                                                year: newStart.year.toString(),
                                                monthsBefore: 0,
                                                monthsAfter: 0,
                                                scenarioSaved: false,
                                            };
                                        }
                                        return context;
                                    }),
                                ],
                                target: 'loading',
                            },
                            'click.nextQuarter': {
                                // show a quarter after
                                // change: quarter, year, start, end
                                // unchanged: x
                                // reset: monthsBefore, monthsAfter
                                // reload: timelineData, filterData
                                actions: [
                                    assign(({ context }) => {
                                        if (context.start) {
                                            const newStart = context.start.plus({ quarters: 1 });
                                            return {
                                                ...context,
                                                start: newStart,
                                                end: calculateEndFromStart(newStart, 0),
                                                quarter: newStart.quarter.toString(),
                                                year: newStart.year.toString(),
                                                monthsBefore: 0,
                                                monthsAfter: 0,
                                                scenarioSaved: false,
                                            };
                                        }
                                        return context;
                                    }),
                                ],
                                target: 'loading',
                            },
                            'click.previousQuarter': {
                                // show a quarter before
                                // change: quarter, year, start, end
                                // unchanged: x
                                // reset: monthsBefore, monthsAfter
                                // reload: timelineData, filterData
                                actions: [
                                    assign(({ context }) => {
                                        if (context.start) {
                                            const newStart = context.start.minus({ quarters: 1 });
                                            return {
                                                ...context,
                                                start: newStart,
                                                end: calculateEndFromStart(newStart, 0),
                                                quarter: newStart.quarter.toString(),
                                                year: newStart.year.toString(),
                                                monthsBefore: 0,
                                                monthsAfter: 0,
                                                scenarioSaved: false,
                                            };
                                        }
                                        return context;
                                    }),
                                ],
                                target: 'loading',
                            },
                            'click.nextMonth': {
                                // show an additional month at the end
                                // change: end, monthsAfter
                                // unchanged: year, quarter, monthsBefore, start
                                // reset: x
                                // reload: timelineData, filterData
                                actions: [
                                    assign({
                                        end: ({ context }) =>
                                            calculateEndFromStart(context.start, context.monthsAfter + 1),
                                        monthsAfter: ({ context }) => context.monthsAfter + 1,
                                        scenarioSaved: () => false,
                                    }),
                                ],
                                target: 'loading',
                            },
                            'click.previousMonth': {
                                // show an additional month at the start
                                // change: start, monthsBefore
                                // unchanged: year, quarter, monthsAfter, end
                                // reset: x
                                // reload: timelineData, filterData
                                actions: [
                                    assign({
                                        start: ({ context }) => context.start?.minus({ months: 1 }),
                                        monthsBefore: ({ context }) => context.monthsBefore + 1,
                                        scenarioSaved: () => false,
                                    }),
                                ],
                                target: 'loading',
                            },
                            'save.scenario': [
                                {
                                    guard: ({ context }) => context.setting === undefined,
                                    target: '#designer.createSetting',
                                },
                                {
                                    guard: ({ context }) => context.setting !== undefined,
                                    target: '#designer.updateSetting',
                                },
                            ],
                            'discard.scenario.changes': {
                                actions: [
                                    assign(({ context }) => {
                                        if (context.setting) {
                                            if (!confirm($t('confirm-discard-setting-changes'))) return context;
                                            const settingStart = DateTime.fromISO(context.setting.start);
                                            const settingEnd = DateTime.fromISO(context.setting.end);
                                            context.eventFilterRef.send({
                                                type: 'undo.changes',
                                                start: settingStart,
                                                end: settingEnd,
                                            });
                                            context.projectFilterRef.send({
                                                type: 'undo.changes',
                                                start: settingStart,
                                                end: settingEnd,
                                            });
                                            context.externalEventFilterRef.send({
                                                type: 'undo.changes',
                                                start: settingStart,
                                                end: settingEnd,
                                            });
                                            return {
                                                ...context,
                                                year: context.setting.year.toString(),
                                                quarter: context.setting.quarter.toString(),
                                                timelineDisplay: context.setting.showSections
                                                    ? 'sections'
                                                    : 'hierarchy',
                                                monthsBefore: context.setting.monthsBefore,
                                                monthsAfter: context.setting.monthsAfter,
                                                start: settingStart,
                                                end: settingEnd,
                                                scenarioSaved: false,
                                            };
                                        }
                                        return context;
                                    }),
                                ],
                                target: 'loading',
                            },
                            'unset.scenario.saved': {
                                actions: assign(({ context }) => {
                                    return {
                                        ...context,
                                        scenarioSaved: false,
                                    };
                                }),
                            },
                        },
                    },
                },
            },
        },
    });
};
